// pages/CompanyPage/CompanyPage.js
import React, { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUI } from '../../contexts/UIContext';
import VideoPreview from '../../components/VideoPreview/VideoPreview';
import NavBar from '../../components/NavBar/NavBar';
import DiscoverSubCategories from '../../components/DiscoverSubCategories/DiscoverSubCategories';
import Footer from '../../components/Footer/Footer';
import { useCompanyPage } from '../../hooks/useCompanyPage';
import styles from './CompanyPage.module.css';

const CompanyPage = () => {
    const { size } = useUI();
    const { companySlug } = useParams();
    const navigate = useNavigate();
    
    const { companyData, isLoading, error } = useCompanyPage(companySlug);

    const handleLinkClick = useCallback((url) => {
        window.open(url, "_blank");
    }, []);

    const handleSeeAllClick = useCallback(() => {
        if (companyData?.categories?.name) {
            navigate(`/discover/${companyData.categories.name.toLowerCase().replace(/\s+/g, "_")}`);
        }
    }, [navigate, companyData?.categories?.name]);

    if (error) {
        return null; // La navegación a 404 ya está manejada en el hook
    }

    if (isLoading) {
        return (
            <div className={styles.loading}>
                Loading...
            </div>
        );
    }

    if (!companyData) {
        return null;
    }

    const { company_overview: companyOverview, formattedAddedTime, categories } = companyData;

    return (
        <div className={styles.pageContainer}>
            <div className={styles.navBarContainer}>
                <NavBar size={size} tickerTextId={1} />
            </div>

            <div className={styles.contentContainer}>
                <div className={`${styles.companyContent} ${styles[size.toLowerCase().replace(" ", "")]}`}>
                    <div 
                        className={styles.videoContainer}
                        onClick={() => handleLinkClick(companyOverview.companylink)}
                    >
                        <VideoPreview
                            companyId={companyData.companyoverviewid}
                            size={size}
                        />
                    </div>

                    <div className={styles.headerContainer}>
                        <h1 className={styles.companyName}>
                            {companyOverview.companyname}
                        </h1>
                        
                        <div className={styles.header}>
                            <div className={styles.category}>
                                <span>{categories.name}</span>
                            </div>
                            
                            <button 
                                className={styles.visitSite}
                                style={{ backgroundColor: companyOverview.button_color }}
                                onClick={() => handleLinkClick(companyOverview.companylink)}
                            >
                                <span>
                                    {size === "Phone" ? "Visit" : "Visit site"}
                                </span>
                            </button>

                            <div className={styles.addedTime}>
                                {formattedAddedTime}
                            </div>
                        </div>
                    </div>

                    <div className={styles.companyInfo}>
                        <div className={styles.description}>
                            {companyOverview.description}
                        </div>

                        <div className={styles.keyFeatures}>
                            {[1, 2, 3, 4, 5].map((num) => (
                                companyOverview[`keyfeature${num}_title`] && (
                                    <div key={`feature-${num}`} className={styles.keyFeature}>
                                        <h3 className={styles.featureTitle}>
                                            {companyOverview[`keyfeature${num}_title`]}
                                        </h3>
                                        <p className={styles.featureDescription}>
                                            {companyOverview[`keyfeature${num}_description`]}
                                        </p>
                                    </div>
                                )
                            ))}
                        </div>
                    </div>

                    <div className={styles.separator} />

                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionTitle}>
                            <span>You might also like</span>
                        </div>
                        <button 
                            className={styles.seeAllButton}
                            onClick={handleSeeAllClick}
                        >
                            <span>See all</span>
                        </button>
                    </div>

                    {companyData.category_id && (
                        <DiscoverSubCategories
                            componentSize={size}
                            selectedCategoryId={companyData.category_id}
                            excludeCardId={companyData.id}
                        />
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default CompanyPage;